import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  IconButton,
  Stack,
  Autocomplete,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Preview as PreviewIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  getEmployees,
  getEmployee,
  getTimesheetSchedules,
  createTimesheetSchedule,
  updateTimesheetSchedule,
  deleteTimesheetSchedule,
  sendTestTimesheetEmail,
  previewTimesheetEmail,
  getMyManagedEmployees,
  getPayPeriodPresets,
  getAllTenants,
} from '../api';

// Report type definitions (shared with Reports.js)
const REPORT_TYPES = [
  { id: 'overall_payroll', label: 'Overall Payroll', color: '#1565C0' },
  { id: 'detailed_timesheet', label: 'Detailed Timesheets', color: '#0D9488' },
  { id: 'employee_summary', label: 'Employee Summary', color: '#7B1FA2' },
  { id: 'outstanding_invoices', label: 'Outstanding Invoices', color: '#E65100' },
  { id: 'outstanding_expenses', label: 'Outstanding Expenses', color: '#AD1457' },
  { id: 'company_hours', label: 'Company Hours', color: '#00695C' },
  { id: 'unpaid_balances', label: 'Unpaid Balances', color: '#C62828' },
  { id: 'payroll_ready_summary', label: 'Payroll Ready Summary', color: '#6A1B9A' },
];

const FREQUENCIES = [
  { value: 'semimonthly', label: 'Semi-Monthly (7 days before payday)' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'daily', label: 'Daily' },
  { value: 'before_payroll', label: 'Before Payroll (auto-calculated)' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' }, { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' }, { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));

// Fallback periods if pay period presets haven't loaded yet
const FALLBACK_PERIODS = [
  { value: 'semimonthly_previous', label: 'Semi-Monthly (Previous Period)' },
  { value: 'semimonthly_current', label: 'Semi-Monthly (Current Period)' },
  { value: 'last_week', label: 'Previous Week' },
  { value: 'this_week', label: 'Current Week' },
  { value: 'biweekly_previous', label: 'Bi-Weekly' },
  { value: 'last_month', label: 'Previous Month' },
  { value: 'this_month', label: 'Current Month' },
];

const SCHEDULE_REPORT_TYPES = REPORT_TYPES.map(rt => ({ value: rt.id, label: rt.label }));

const initialScheduleForm = {
  tenant_id: '',
  employee_id: '',
  frequency: 'semimonthly',
  day_of_week: 1,
  day_of_month: 1,
  time_of_day: '08:00',
  recipients: '',
  include_manager: true,
  include_employee: false,
  include_prior_unpaid: false,
  pay_entries_only: false,
  payroll_ready_only: false,
  report_period: 'semimonthly_previous',
  is_active: true,
  email_subject: '',
  email_body: '',
  report_type: 'detailed_timesheet',
  days_before_payroll: 7,
};

export default function AutomatedReports() {
  const { user, isProgramAdmin, hasPermission } = useAuth();
  // Mirrors the server's accessibleEmployeeIds(): whole tenant vs direct reports + self
  const wholeTenant = isProgramAdmin || ['admin', 'payroll_admin'].includes(user?.role) || hasPermission('can_view_all_employees');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [employees, setEmployees] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [reportPeriods, setReportPeriods] = useState(FALLBACK_PERIODS);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(initialScheduleForm);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setScheduleLoading(true);
      const [empRes, selfRes, schedRes, presetsRes, tenantRes] = await Promise.all([
        wholeTenant ? getEmployees() : getMyManagedEmployees().catch(() => ({ data: [] })),
        // Scoped users may also schedule themselves (server scope = direct reports + self).
        !wholeTenant && user?.employeeId ? getEmployee(user.employeeId).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        getTimesheetSchedules(),
        getPayPeriodPresets().catch(() => ({ data: [] })),
        isProgramAdmin ? getAllTenants().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      const empList = [...(empRes.data || [])];
      const me = selfRes.data;
      if (me && me.id && !empList.some(e => String(e.employee_id || e.id) === String(me.id))) {
        empList.push({ employee_id: me.id, first_name: me.first_name, last_name: me.last_name, email: me.email });
      }
      setEmployees(empList);
      setTenants((tenantRes.data || []).filter(t => t.is_active));
      setSchedules(schedRes.data || []);
      // Build report period options from pay period presets (skip "All Time" — not useful for scheduled reports)
      if (presetsRes.data && presetsRes.data.length > 0) {
        const periods = presetsRes.data
          .filter(p => p.period_type !== 'all_time')
          .map(p => ({ value: p.period_type, label: p.name }));
        if (periods.length > 0) setReportPeriods(periods);
      }
    } catch (err) {
      console.error('Failed to load schedule data:', err);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleOpenScheduleDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      const emp = employees.find(e => String(e.employee_id || e.id) === String(schedule.employee_id));
      setSelectedEmployee(emp || null);
      setScheduleForm({
        tenant_id: String(schedule.tenant_id || ''),
        employee_id: String(schedule.employee_id || ''),
        frequency: schedule.frequency,
        day_of_week: schedule.day_of_week ?? 1,
        day_of_month: schedule.day_of_month ?? 1,
        time_of_day: schedule.time_of_day?.substring(0, 5) || '08:00',
        recipients: schedule.recipients || '',
        include_manager: schedule.include_manager,
        include_employee: schedule.include_employee,
        include_prior_unpaid: schedule.include_prior_unpaid || false,
        pay_entries_only: schedule.pay_entries_only || false,
        payroll_ready_only: schedule.payroll_ready_only || false,
        report_period: schedule.report_period || 'semimonthly_previous',
        is_active: schedule.is_active,
        email_subject: schedule.email_subject || '',
        email_body: schedule.email_body || '',
        report_type: schedule.report_type || 'detailed_timesheet',
      });
    } else {
      setEditingSchedule(null);
      setSelectedEmployee(null);
      setScheduleForm(initialScheduleForm);
    }
    setOpenDialog(true);
  };

  const handleCloseScheduleDialog = () => {
    setOpenDialog(false);
    setEditingSchedule(null);
    setSelectedEmployee(null);
    setScheduleForm(initialScheduleForm);
  };

  const handleScheduleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setScheduleForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const scheduleNeedsEmployee = ['detailed_timesheet', 'overall_payroll', 'unpaid_balances'].includes(scheduleForm.report_type);

  const handleScheduleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const employeeId = selectedEmployee ? String(selectedEmployee.employee_id || selectedEmployee.id) : scheduleForm.employee_id;

      if (isProgramAdmin && !editingSchedule && !scheduleForm.tenant_id) {
        setError('Please select a tenant');
        setSubmitting(false);
        return;
      }

      if (scheduleNeedsEmployee && !employeeId) {
        setError('Please select an employee for this report type');
        setSubmitting(false);
        return;
      }

      const payload = {
        ...scheduleForm,
        employee_id: scheduleNeedsEmployee ? employeeId : (employeeId || null),
        day_of_week: ['weekly', 'biweekly'].includes(scheduleForm.frequency) ? scheduleForm.day_of_week : null,
        day_of_month: scheduleForm.frequency === 'monthly' ? scheduleForm.day_of_month : null,
      };

      if (isProgramAdmin && !editingSchedule) {
        payload.tenant_id = Number(scheduleForm.tenant_id);
      } else {
        delete payload.tenant_id;
      }

      if (editingSchedule) {
        await updateTimesheetSchedule(editingSchedule.id, payload);
        setSuccess('Schedule updated successfully');
      } else {
        await createTimesheetSchedule(payload);
        setSuccess('Schedule created successfully');
      }
      handleCloseScheduleDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await deleteTimesheetSchedule(id);
      setSuccess('Schedule deleted');
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      setError('Failed to delete schedule');
    }
  };

  const handleSendTest = async (schedule) => {
    try {
      setError('');
      await sendTestTimesheetEmail(schedule.id);
      setSuccess('Test email sent!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
    }
  };

  const handlePreview = async (schedule) => {
    try {
      setPreviewLoading(true);
      setPreviewDialog(true);
      const res = await previewTimesheetEmail(schedule.id);
      setPreviewContent(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preview');
      setPreviewDialog(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Preview, Send test and Edit on someone else's schedule need View pay rates (the email is built with their pay visibility)
  const canActOn = (s) => s.manager_user_id === user?.id || isProgramAdmin || ['admin', 'payroll_admin'].includes(user?.role) || hasPermission('can_view_pay_rates');
  const NEEDS_PAY_TIP = "Needs View pay rates: this schedule's email can include pay";

  const formatScheduleLabel = (schedule) => {
    const time = schedule.time_of_day?.substring(0, 5) || '08:00';
    const dayName = DAYS_OF_WEEK.find(d => d.value === schedule.day_of_week)?.label || '';
    switch (schedule.frequency) {
      case 'daily': return `Daily at ${time}`;
      case 'weekly': return `Every ${dayName} at ${time}`;
      case 'biweekly': return `Every other ${dayName} at ${time}`;
      case 'semimonthly': return `Semi-monthly (8th & 23rd) at ${time}`;
      case 'monthly': return `Day ${schedule.day_of_month} monthly at ${time}`;
      default: return schedule.frequency;
    }
  };

  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'Pending';
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getReportTypeLabel = (type) => {
    const found = REPORT_TYPES.find(r => r.id === type);
    return found ? found.label : type || 'Detailed Timesheet';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <ScheduleIcon sx={{ mr: 1 }} />
        <Typography variant={isMobile ? 'h6' : 'h5'}>Automated Reports</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Email Schedules
            <Chip label={schedules.length} size="small" color="primary" />
          </Typography>
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {employees.length === 0 && !wholeTenant && (
                <Typography variant="caption" color="text.secondary">
                  No managed employees assigned — contact an admin
                </Typography>
              )}
              <Tooltip
                title={employees.length === 0 && !wholeTenant
                  ? 'You have no employees assigned to manage. Ask an admin to assign direct reports before creating a schedule.'
                  : ''}
              >
                <span>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenScheduleDialog()} disabled={employees.length === 0} size="small">
                    New Schedule
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {scheduleLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : schedules.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No automated schedules configured yet</Typography>
        ) : isMobile ? (
          /* Mobile Card View */
          <Stack spacing={1.5}>
            {schedules.map((schedule) => (
              <Card key={schedule.id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Chip label={getReportTypeLabel(schedule.report_type)} size="small" sx={{ mb: 0.5, bgcolor: REPORT_TYPES.find(r => r.id === schedule.report_type)?.color || '#666', color: 'white' }} />
                      {schedule.first_name && (
                        <Typography variant="body2" fontWeight="medium">
                          {schedule.first_name} {schedule.last_name}
                        </Typography>
                      )}
                      {wholeTenant && (
                        <Typography variant="caption" color="text.secondary" display="block">Created by {schedule.manager_username || '—'}</Typography>
                      )}
                      {isProgramAdmin && (
                        <Typography variant="caption" color="text.secondary">{schedule.tenant_name || '—'}</Typography>
                      )}
                    </Box>
                    {schedule.is_active ? (
                      <Chip icon={<ActiveIcon />} label="Active" color="success" size="small" />
                    ) : (
                      <Chip icon={<InactiveIcon />} label="Paused" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">{formatScheduleLabel(schedule)}</Typography>
                  <Typography variant="body2" color="text.secondary">Next: {formatNextRun(schedule.next_run_at)}</Typography>
                  <Box display="flex" justifyContent="flex-end" gap={0.5} mt={1} flexWrap="wrap">
                    <Button size="small" startIcon={<PreviewIcon />} disabled={!canActOn(schedule)} onClick={() => handlePreview(schedule)}>Preview</Button>
                    <Button size="small" startIcon={<SendIcon />} disabled={!canActOn(schedule)} onClick={() => handleSendTest(schedule)}>Test</Button>
                    <Button size="small" startIcon={<EditIcon />} disabled={!canActOn(schedule)} onClick={() => handleOpenScheduleDialog(schedule)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirm(schedule)}>Delete</Button>
                  </Box>
                  {!canActOn(schedule) && (
                    <Typography variant="caption" color="text.secondary" display="block" align="right">{NEEDS_PAY_TIP}</Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          /* Desktop Table View */
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Report Type</TableCell>
                  {isProgramAdmin && <TableCell>Tenant</TableCell>}
                  <TableCell>Employee</TableCell>
                  {wholeTenant && (
                    <TableCell>
                      Created by
                      <Tooltip title="Each email is built with this person's permissions, checked every time it sends: no pay amounts if they can't view pay rates, and no email once the employee is no longer theirs or their account is deactivated.">
                        <InfoIcon fontSize="inherit" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Next Run</TableCell>
                  <TableCell>Last Sent</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Chip label={getReportTypeLabel(schedule.report_type)} size="small"
                        sx={{ bgcolor: REPORT_TYPES.find(r => r.id === schedule.report_type)?.color || '#666', color: 'white', fontSize: '0.7rem' }} />
                    </TableCell>
                    {isProgramAdmin && <TableCell>{schedule.tenant_name || '—'}</TableCell>}
                    <TableCell>
                      {schedule.first_name ? `${schedule.first_name} ${schedule.last_name}` : 'N/A'}
                    </TableCell>
                    {wholeTenant && <TableCell>{schedule.manager_username || '—'}</TableCell>}
                    <TableCell>{formatScheduleLabel(schedule)}</TableCell>
                    <TableCell>
                      {schedule.is_active ? (
                        <Chip icon={<ActiveIcon />} label="Active" color="success" size="small" />
                      ) : (
                        <Chip icon={<InactiveIcon />} label="Paused" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatNextRun(schedule.next_run_at)}</TableCell>
                    <TableCell>{schedule.last_sent_at ? new Date(schedule.last_sent_at).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={canActOn(schedule) ? 'Preview' : NEEDS_PAY_TIP}><span><IconButton size="small" disabled={!canActOn(schedule)} onClick={() => handlePreview(schedule)}><PreviewIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title={canActOn(schedule) ? 'Send test' : NEEDS_PAY_TIP}><span><IconButton size="small" disabled={!canActOn(schedule)} onClick={() => handleSendTest(schedule)}><SendIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title={canActOn(schedule) ? 'Edit' : NEEDS_PAY_TIP}><span><IconButton size="small" disabled={!canActOn(schedule)} onClick={() => handleOpenScheduleDialog(schedule)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm(schedule)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Mobile FAB for new schedule */}
        {isMobile && employees.length > 0 && (
          <Fab color="primary" sx={{ position: 'fixed', bottom: 72, right: 16 }} onClick={() => handleOpenScheduleDialog()}>
            <AddIcon />
          </Fab>
        )}
      </Paper>

      {/* Schedule Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseScheduleDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'New Automated Schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isProgramAdmin && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth required disabled={!!editingSchedule}>
                    <InputLabel>Tenant</InputLabel>
                    <Select name="tenant_id" value={scheduleForm.tenant_id} label="Tenant"
                      onChange={(e) => { setSelectedEmployee(null); setScheduleForm(prev => ({ ...prev, tenant_id: e.target.value, employee_id: '' })); }}>
                      {editingSchedule && !tenants.some(t => String(t.id) === String(editingSchedule.tenant_id)) && (
                        <MenuItem value={String(editingSchedule.tenant_id)}>{editingSchedule.tenant_name || `Tenant ${editingSchedule.tenant_id}`}</MenuItem>
                      )}
                      {tenants.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.display_name || t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Tooltip title="Program admins work across tenants, so every schedule belongs to one tenant. The report reads only that tenant's data and is emailed through that tenant's email settings. The tenant can't be changed after the schedule is created.">
                    <IconButton size="small" aria-label="About the tenant"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Report Type</InputLabel>
                <Select name="report_type" value={scheduleForm.report_type} onChange={handleScheduleFormChange} label="Report Type">
                  {SCHEDULE_REPORT_TYPES.filter(rt => wholeTenant || rt.value !== 'payroll_ready_summary' || rt.value === scheduleForm.report_type).map(rt => (
                    <MenuItem key={rt.value} value={rt.value}>{rt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {scheduleNeedsEmployee && (
              <Grid item xs={12}>
                <Autocomplete
                  options={isProgramAdmin ? employees.filter(e => String(e.tenant_id) === String(scheduleForm.tenant_id)) : employees}
                  getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ''}
                  value={selectedEmployee}
                  onChange={(event, newValue) => {
                    setSelectedEmployee(newValue);
                    setScheduleForm(prev => ({
                      ...prev,
                      employee_id: newValue ? String(newValue.employee_id || newValue.id) : '',
                      // A salaried employee's contract hours are not paid separately: default the report to pay entries only
                      ...(!editingSchedule ? { pay_entries_only: newValue?.employee_type === 'salary' } : {})
                    }));
                  }}
                  disabled={!!editingSchedule || (isProgramAdmin && !scheduleForm.tenant_id)}
                  renderInput={(params) => <TextField {...params} label="Employee" required
                    helperText={isProgramAdmin && !scheduleForm.tenant_id ? 'Pick a tenant first' : undefined} />}
                  isOptionEqualToValue={(option, value) => (option?.employee_id || option?.id) === (value?.employee_id || value?.id)}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select name="frequency" value={scheduleForm.frequency} onChange={handleScheduleFormChange} label="Frequency">
                  {FREQUENCIES.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {['weekly', 'biweekly'].includes(scheduleForm.frequency) && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Day of Week</InputLabel>
                  <Select name="day_of_week" value={scheduleForm.day_of_week} onChange={handleScheduleFormChange} label="Day of Week">
                    {DAYS_OF_WEEK.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {scheduleForm.frequency === 'monthly' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Day of Month</InputLabel>
                  <Select name="day_of_month" value={scheduleForm.day_of_month} onChange={handleScheduleFormChange} label="Day of Month">
                    {DAYS_OF_MONTH.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {scheduleForm.frequency === 'before_payroll' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Business Days Before Payroll" name="days_before_payroll" type="number"
                  value={scheduleForm.days_before_payroll} onChange={handleScheduleFormChange}
                  InputProps={{ inputProps: { min: 1, max: 30 } }}
                  helperText="Number of business days before payroll to send the reminder" />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Time of Day" name="time_of_day" type="time" value={scheduleForm.time_of_day} onChange={handleScheduleFormChange} InputLabelProps={{ shrink: true }} />
            </Grid>

            {scheduleForm.report_type !== 'payroll_ready_summary' && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Report Period</InputLabel>
                <Select name="report_period" value={scheduleForm.report_period} onChange={handleScheduleFormChange} label="Report Period">
                  {reportPeriods.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            )}

            <Grid item xs={12}>
              <TextField fullWidth label="Additional Recipients" name="recipients" value={scheduleForm.recipients} onChange={handleScheduleFormChange}
                placeholder="email1@example.com, email2@example.com" helperText="Comma-separated email addresses" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Custom Email Subject" name="email_subject" value={scheduleForm.email_subject} onChange={handleScheduleFormChange}
                placeholder="HourTrax Report: {employee_name} ({period_start} - {period_end})"
                helperText="Leave blank for default. Variables: {employee_name}, {period_start}, {period_end}, {total_hours}, {total_pay} (unpaid balance), {invoiced_total}, {client_paid_total}" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Custom Email Body" name="email_body" value={scheduleForm.email_body} onChange={handleScheduleFormChange}
                placeholder="Please review the attached timesheet for {employee_name}..."
                helperText="Optional message added above the report. Variables: {employee_name}, {period_start}, {period_end}, {total_hours}, {total_pay} (unpaid balance), {invoiced_total}, {client_paid_total}"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <Tooltip title="Free-form text maintained by the admin. It is not updated when HourTrax changes, so review it whenever your payroll process changes. The variables named under the field are replaced when the email is sent.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel control={<Switch name="include_manager" checked={scheduleForm.include_manager} onChange={handleScheduleFormChange} />} label="Send to my email (manager)" />
            </Grid>
            {scheduleNeedsEmployee && (
              <Grid item xs={12}>
                <FormControlLabel control={<Switch name="include_employee" checked={scheduleForm.include_employee} onChange={handleScheduleFormChange} />} label="Send to employee's email" />
              </Grid>
            )}
            {scheduleNeedsEmployee && (
              <Grid item xs={12}>
                <FormControlLabel control={<Switch name="include_prior_unpaid" checked={scheduleForm.include_prior_unpaid} onChange={handleScheduleFormChange} />}
                  label="Include historical unpaid hours" />
              </Grid>
            )}
            {scheduleNeedsEmployee && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel control={<Switch name="pay_entries_only" checked={scheduleForm.pay_entries_only} onChange={handleScheduleFormChange} />}
                    label="Pay entries only" />
                  <Tooltip title="Leave out salary-covered entries (Pay Basis: Salary-covered, such as a salaried employee's contract hours). The Hours total then counts only hours that are paid separately. Turned on automatically when you pick a salaried employee for a new schedule.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel control={<Switch name="payroll_ready_only" checked={scheduleForm.payroll_ready_only} onChange={handleScheduleFormChange} />}
                    label="Payroll ready entries only" />
                  <Tooltip title="With the payment pipeline on, list only entries at the Payroll ready stage (client has paid). Pending and invoiced entries are left out until the client pays.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch name="is_active" checked={scheduleForm.is_active} onChange={handleScheduleFormChange} />} label="Active (schedule is running)" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleScheduleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : (editingSchedule ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : previewContent ? (
            <Box>
              <Box mb={2} p={2} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="body2"><strong>Employee:</strong> {previewContent.employee}</Typography>
                <Typography variant="body2"><strong>Period:</strong> {previewContent.period.startDate} to {previewContent.period.endDate}</Typography>
                {previewContent.summary.entries > 0 && (
                  <Typography variant="body2">
                    <strong>Entries:</strong> {previewContent.summary.entries} |
                    <strong> Hours:</strong> {previewContent.summary.totalHours?.toFixed(2) || '0'} |
                    {previewContent.summary.pay_hidden ? (
                      <>
                        <strong> Pay:</strong> hidden
                        <Tooltip title="The person who created this schedule can't view pay rates, so the email leaves out every amount.">
                          <InfoIcon fontSize="inherit" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        </Tooltip>
                      </>
                    ) : (
                      <><strong> Unpaid:</strong> ${previewContent.summary.totalPay?.toFixed(2) || '0'}</>
                    )}
                    {previewContent.summary.payEntriesOnly
                      ? ' | Salary-covered entries left out'
                      : (previewContent.summary.salaryCoveredHours > 0 && ` | Salary-covered: ${previewContent.summary.salaryCoveredHours.toFixed(2)} h`)}
                  </Typography>
                )}
              </Box>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <iframe sandbox="" srcDoc={previewContent.html} title="Email Preview" style={{ width: '100%', height: '400px', border: 'none' }} />
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreviewDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Schedule?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this schedule
            {deleteConfirm?.first_name ? ` for ${deleteConfirm.first_name} ${deleteConfirm.last_name}` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" onClick={() => handleDeleteSchedule(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} message={success} />
    </Box>
  );
}
